import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowDownToLine,
  Bell,
  FileText,
  LayoutDashboard,
  LogOut,
  TrendingUp,
  User,
} from "lucide-react";
import { Logo } from "../Logo";
import { ThemeToggle } from "../ThemeToggle";
import { AppFooter } from "./AppFooter";
import { Button } from "../ui/button";
import { useAuth } from "../../context/AuthContext";
import { api, mediaUrl } from "../../lib/api";
import { initials } from "../../lib/format";
import { cn } from "../../lib/utils";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/investments", label: "Investments", icon: TrendingUp, testId: "nav-investments" },
  { to: "/withdrawals", label: "Withdrawals", icon: ArrowDownToLine, testId: "nav-withdrawals" },
  { to: "/notifications", label: "Updates", icon: Bell, testId: "nav-notifications" },
  { to: "/statement", label: "Statement", icon: FileText, testId: "nav-statement" },
  { to: "/profile", label: "Profile", icon: User, testId: "nav-profile" },
];

const linkClass = ({ isActive }) =>
  cn(
    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
    isActive
      ? "bg-primary/10 text-primary"
      : "text-muted-foreground hover:bg-accent hover:text-foreground"
  );

export const ClientLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let active = true;
    api
      .get("/api/client/notifications", { params: { limit: 1 } })
      .then(({ data }) => {
        if (active) setUnread(data.unread || 0);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const photo = mediaUrl(user?.photo);

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <aside className="surface-1 fixed inset-y-0 left-0 z-40 hidden w-60 flex-col border-r border-border md:flex">
        <div className="flex h-14 items-center border-b border-border px-4">
          <Logo size="h-6" to="/dashboard" />
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map(({ to, label, icon: Icon, testId }) => (
            <NavLink key={to} to={to} className={linkClass} data-testid={testId}>
              <Icon className="h-4 w-4" />
              <span className="flex-1">{label}</span>
              {to === "/notifications" && unread > 0 ? (
                <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                  {unread}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <Button
            variant="ghost"
            className="w-full justify-start text-muted-foreground"
            onClick={handleLogout}
            data-testid="sidebar-logout-button"
          >
            <LogOut className="mr-3 h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      <div className="md:pl-60">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-3 border-b border-border bg-background px-4 sm:px-6">
          <span className="md:hidden">
            <Logo size="h-6" to="/dashboard" />
          </span>
          <div className="hidden min-w-0 md:block">
            <p className="truncate text-sm font-medium">Welcome back, {user?.name?.split(" ")[0]}</p>
            <p className="text-xs text-muted-foreground">Investor portal</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Link
              to="/notifications"
              className="relative inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              data-testid="header-notifications-link"
            >
              <Bell className="h-4 w-4" />
              {unread > 0 ? (
                <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-primary" />
              ) : null}
            </Link>
            <ThemeToggle />
            <Link
              to="/profile"
              className="ml-1 flex h-8 w-8 items-center justify-center overflow-hidden rounded-full border border-border bg-secondary text-xs font-semibold"
              data-testid="header-profile-link"
            >
              {photo ? (
                <img src={photo} alt={user?.name} className="h-full w-full object-cover" />
              ) : (
                initials(user?.name)
              )}
            </Link>
          </div>
        </header>

        <main className="px-4 py-5 pb-24 sm:px-6 sm:py-6 md:pb-6">
          <Outlet />
        </main>
        <div className="hidden md:block">
          <AppFooter />
        </div>
      </div>

      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-5 border-t border-border bg-background md:hidden">
        {NAV.slice(0, 4)
          .concat(NAV[5])
          .map(({ to, label, icon: Icon, testId }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  "relative flex min-h-14 flex-col items-center justify-center gap-1 text-[11px] transition-colors",
                  isActive ? "text-primary" : "text-muted-foreground"
                )
              }
              data-testid={`mobile-${testId}`}
            >
              {({ isActive }) => (
                <>
                  {isActive ? (
                    <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-primary" />
                  ) : null}
                  <span className="relative">
                    <Icon className="h-5 w-5" />
                    {to === "/notifications" && unread > 0 ? (
                      <span className="absolute -right-1.5 -top-1 h-2 w-2 rounded-full bg-primary" />
                    ) : null}
                  </span>
                  {label}
                </>
              )}
            </NavLink>
          ))}
      </nav>
    </div>
  );
};
